import { useCallback, useRef } from 'react';

import { usePlannerAccount } from '@trello/business-logic-react/planner';

import { useTrelloTogglePlannerCalendarMutation } from './TrelloTogglePlannerCalendarMutation.generated';
import { useWorkspaceAri } from './useWorkspaceAri';

/**
 * Toggles a calendar's visibility on or off.
 *
 * @returns - A function that handles the calendar toggling.
 * @returns {string | undefined} - The calendar id of the calendar being toggled.
 */

export const useToggleCalendar = () => {
  const { workspaceAri } = useWorkspaceAri();
  const { plannerAccountId, enabledCalendars, accountType } =
    usePlannerAccount();

  const [togglePlannerCalendar, { loading }] =
    useTrelloTogglePlannerCalendarMutation();
  const toggledCalendarId = useRef<string | undefined>(undefined);

  const toggleCalendarMutation = useCallback(
    async ({
      providerCalendarId,
      start,
      end,
      newCheckedState,
      traceId,
    }: {
      providerCalendarId: string;
      start: string;
      end: string;
      newCheckedState: boolean;
      traceId?: string;
    }): Promise<void> => {
      if (loading || !plannerAccountId || !workspaceAri) {
        return;
      }

      const calendarId = enabledCalendars?.find(
        (edge) => edge?.providerCalendarId === providerCalendarId,
      )?.id;

      const toggledCalendar = await togglePlannerCalendar({
        variables: {
          enabled: newCheckedState,
          end,
          providerAccountId: plannerAccountId,
          providerCalendarId,
          workspaceId: workspaceAri,
          start,
          type: accountType === 'OUTLOOK' ? 'OUTLOOK' : 'GOOGLE',
        },
        context: {
          traceId,
        },
        // Apollo doesn't allow you to optimistically use cache.evict so we have to use modify to evict
        // https://github.com/apollographql/apollo-client/issues/10289
        ...(!newCheckedState &&
          calendarId && {
            optimisticResponse: {
              __typename: 'Mutation',
              trello: {
                __typename: 'TrelloMutationApi',
                createOrUpdatePlannerCalendar: {
                  errors: null,
                  plannerCalendarMutated: {
                    __typename: 'TrelloPlannerCalendarDeleted',
                    id: calendarId,
                  },
                  success: true,
                  __typename: 'TrelloCreateOrUpdatePlannerCalendarPayload',
                },
              },
            },
            update: (cache, { data }) => {
              if (data?.trello?.createOrUpdatePlannerCalendar?.success) {
                cache.modify({
                  id: cache.identify({
                    __typename: 'TrelloPlannerCalendar',
                    id: calendarId,
                  }),
                  fields: (_, { DELETE }) => DELETE,
                });
              }
            },
          }),
      });

      // Dont set the toggled calendar id if we are toggling off a calendar
      if (
        !(
          toggledCalendar.data?.trello.createOrUpdatePlannerCalendar
            ?.plannerCalendarMutated?.__typename ===
          'TrelloPlannerCalendarDeleted'
        )
      ) {
        toggledCalendarId.current =
          toggledCalendar.data?.trello.createOrUpdatePlannerCalendar?.plannerCalendarMutated?.enabledCalendars?.edges?.[0].node?.id;
      }
    },
    [
      loading,
      plannerAccountId,
      workspaceAri,
      enabledCalendars,
      togglePlannerCalendar,
      accountType,
    ],
  );
  return { toggleCalendarMutation, toggledCalendarId };
};
